import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../utils/usePermissions';
import './DetailPack.css';

const API_URL = import.meta.env.VITE_API_URL;

// Helper prix
const getPrixVente = (produit) => {
    if (!produit) return 0;
    if (produit.modeGestionStock === 'lineaire') {
        const base = produit.prixMetre || 0;
        return base + base * (produit.tauxGain || 0) / 100;
    }
    const base = produit.prixAchat || 0;
    return base + base * (produit.tauxGain || 0) / 100;
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const DetailPack = ({ packId: propPackId, onNavigate, onLogout, currentPage }) => {
    const { hasPermission } = usePermissions();

    // Extraire l'ID du pack (prop direct ou paramètre de requête URL ?id=... ou ?packId=...)
    const getQueryParam = (key) => new URLSearchParams(window.location.search).get(key);
    const packId = propPackId || getQueryParam('id') || getQueryParam('packId');

    const [pack, setPack] = useState(null);
    const [stockInfo, setStockInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stockLoading, setStockLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`
    });

    const fetchPackDetails = useCallback(async () => {
        if (!packId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/packs/${packId}`, {
                headers: getAuthHeaders()
            });
            setPack(res.data);
        } catch (err) {
            console.error('Erreur chargement détail pack:', err);
            Swal.fire('Erreur', 'Impossible de charger les détails du pack.', 'error');
        } finally {
            setLoading(false);
        }
    }, [packId]);

    const fetchStockStatus = useCallback(async () => {
        if (!packId) return;
        setStockLoading(true);
        try {
            const res = await axios.get(`${API_URL}/packs/${packId}/verifier-stock`, {
                headers: getAuthHeaders()
            });
            setStockInfo(res.data);
        } catch (err) {
            console.error('Erreur vérification stock pack:', err);
        } finally {
            setStockLoading(false);
        }
    }, [packId]);

    useEffect(() => {
        fetchPackDetails();
        fetchStockStatus();
    }, [fetchPackDetails, fetchStockStatus]);

    // ── Actions ────────────────────────────────────────────────────────────────
    const handleToggleStatus = async () => {
        if (!hasPermission('produits', 'update')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de modifier ce pack.", 'warning');
            return;
        }
        const nextStatus = pack.statut === 'actif' ? 'inactif' : 'actif';
        setActionLoading(true);
        try {
            await axios.put(`${API_URL}/packs/${pack._id}`, { statut: nextStatus }, {
                headers: getAuthHeaders()
            });
            setPack(prev => ({ ...prev, statut: nextStatus }));
            Swal.fire({
                title: 'Statut mis à jour !',
                text: `Le pack est maintenant ${nextStatus === 'actif' ? 'Actif' : 'Inactif'}.`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err) {
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors du changement de statut.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!hasPermission('produits', 'delete')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de supprimer ce pack.", 'warning');
            return;
        }
        const result = await Swal.fire({
            title: 'Supprimer ce pack ?',
            text: `Êtes-vous sûr de vouloir supprimer définitivement le pack "${pack.nom}" ? Cette action est irréversible.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e11d48',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${API_URL}/packs/${pack._id}`, { headers: getAuthHeaders() });
                await Swal.fire('Supprimé !', 'Le pack a été supprimé avec succès.', 'success');
                onNavigate('packsAdmin');
            } catch (err) {
                Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la suppression.', 'error');
            }
        }
    };

    const handleEdit = () => {
        if (!hasPermission('produits', 'update')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de modifier ce pack.", 'warning');
            return;
        }
        onNavigate('packsAdmin', { editId: pack._id });
    };

    const handleDuplicate = () => {
        if (!hasPermission('produits', 'create')) {
            Swal.fire('Accès refusé', "Vous n'avez pas la permission de dupliquer ce pack.", 'warning');
            return;
        }
        onNavigate('packsAdmin', { duplicateId: pack._id });
    };

    // ── Calculs financiers dynamiques ──────────────────────────────────────────
    const financialSummary = React.useMemo(() => {
        if (!pack || !pack.categories) {
            return { sousTotalHT: 0, totalTVA: 0, totalTTC: 0, prixFinal: 0, delta: 0, rows: [] };
        }

        let sousTotalHT = 0;
        let totalTVA = 0;
        const rows = [];

        for (const cat of pack.categories) {
            for (const item of cat.produits) {
                let nomProd = 'Ligne libre';
                let refProd = '—';
                let marqueProd = '—';
                let catLabel = cat.categorieId?.label || 'Général';
                let prixUnitaireHT = 0;
                let tvaRate = 19;
                let qte = item.quantiteRecommandee || 1;
                let imageProd = null;
                let isLineaire = false;
                let decoupe = item.decoupeLineaire;

                if (item.produitId) {
                    const prod = item.produitId;
                    nomProd = prod.nomProduit || 'Produit';
                    refProd = prod.reference || '—';
                    marqueProd = prod.marque?.name || '—';
                    prixUnitaireHT = getPrixVente(prod);
                    tvaRate = prod.TVA !== undefined ? Number(prod.TVA) : 19;
                    imageProd = prod.imageProduit;
                    isLineaire = prod.modeGestionStock === 'lineaire';
                } else {
                    prixUnitaireHT = Number(item.prixUnitaire) || 0;
                    nomProd = item.nomProduit || 'Ligne libre';
                }

                const itemTotalHT = prixUnitaireHT * qte;
                const itemTVA = itemTotalHT * (tvaRate / 100);
                const itemTotalTTC = itemTotalHT + itemTVA;

                sousTotalHT += itemTotalHT;
                totalTVA += itemTVA;

                rows.push({
                    item,
                    nomProd,
                    refProd,
                    marqueProd,
                    catLabel,
                    prixUnitaireHT,
                    tvaRate,
                    qte,
                    imageProd,
                    isLineaire,
                    decoupe,
                    itemTotalHT,
                    itemTVA,
                    itemTotalTTC
                });
            }
        }

        const totalTTC = sousTotalHT + totalTVA;
        const prixFinal = pack.prixFinal ?? pack.prixCalcule ?? totalTTC;
        const delta = prixFinal - totalTTC;

        return { sousTotalHT, totalTVA, totalTTC, prixFinal, delta, rows };
    }, [pack]);

    if (loading) {
        return (
            <div className="dp-layout">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dp-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (!pack) {
        return (
            <div className="dp-layout">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dp-content">
                    <div className="dp-error-card">
                        <h2>⚠️ Pack introuvable</h2>
                        <p>Le pack demandé n'existe pas ou a été supprimé.</p>
                        <button className="dp-btn-back" onClick={() => onNavigate('packsAdmin')}>
                            ← Retour aux packs
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dp-layout">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <div className="dp-content">
                {/* ── Top Bar & Navigation ── */}
                <div className="dp-top-actions-bar">
                    <button className="dp-btn-back" onClick={() => onNavigate('packsAdmin')}>
                        ← Retour à la liste
                    </button>
                    <div className="dp-action-buttons">
                        {hasPermission('produits', 'update') && (
                            <button className="dp-btn-action edit" onClick={handleEdit} disabled={actionLoading}>
                                ✏️ Modifier
                            </button>
                        )}
                        {hasPermission('produits', 'create') && (
                            <button className="dp-btn-action duplicate" onClick={handleDuplicate} disabled={actionLoading}>
                                📋 Dupliquer
                            </button>
                        )}
                        {hasPermission('produits', 'update') && (
                            <button
                                className={`dp-btn-action toggle ${pack.statut === 'actif' ? 'deactivate' : 'activate'}`}
                                onClick={handleToggleStatus}
                                disabled={actionLoading}
                            >
                                {pack.statut === 'actif' ? '⏸️ Désactiver' : '▶️ Activer'}
                            </button>
                        )}
                        {hasPermission('produits', 'delete') && (
                            <button className="dp-btn-action delete" onClick={handleDelete} disabled={actionLoading}>
                                🗑️ Supprimer
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Header Card ── */}
                <div className="dp-hero-card">
                    <div className="dp-hero-header">
                        <div className="dp-hero-titles">
                            <div className="dp-hero-badges">
                                <span className="dp-badge-power">⚡ {pack.puissance}</span>
                                <span className="dp-badge-ref">Ref: {pack.reference || 'PK-NON-DEFINI'}</span>
                                <span className={`dp-badge-status ${pack.statut}`}>
                                    {pack.statut === 'actif' ? '● Actif' : '○ Inactif'}
                                </span>
                            </div>
                            <h1 className="dp-pack-title">{pack.nom}</h1>
                        </div>
                        <div className="dp-hero-price-tag">
                            <span className="dp-price-label">Prix Final TTC</span>
                            <span className="dp-price-val">{fmt(financialSummary.prixFinal)} DT</span>
                            <span className="dp-price-sub">
                                Calculé : {fmt(financialSummary.totalTTC)} DT
                            </span>
                        </div>
                    </div>

                    {pack.description && (
                        <div className="dp-hero-description">
                            <p>{pack.description}</p>
                        </div>
                    )}

                    <div className="dp-hero-meta-grid">
                        <div className="dp-meta-item">
                            <span className="meta-icon">📅</span>
                            <div>
                                <small>Date de création</small>
                                <strong>{new Date(pack.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
                            </div>
                        </div>
                        <div className="dp-meta-item">
                            <span className="meta-icon">🔄</span>
                            <div>
                                <small>Dernière modification</small>
                                <strong>{new Date(pack.updatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
                            </div>
                        </div>
                        <div className="dp-meta-item">
                            <span className="meta-icon">👤</span>
                            <div>
                                <small>Créateur</small>
                                <strong>
                                    {pack.createdBy
                                        ? `${pack.createdBy.prenom || ''} ${pack.createdBy.nom || ''}`.trim() || pack.createdBy.email || pack.createdBy.username
                                        : 'Système / Admin'}
                                </strong>
                            </div>
                        </div>
                        {pack.updatedBy && (
                            <div className="dp-meta-item">
                                <span className="meta-icon">✏️</span>
                                <div>
                                    <small>Modifié par</small>
                                    <strong>
                                        {`${pack.updatedBy.prenom || ''} ${pack.updatedBy.nom || ''}`.trim() || pack.updatedBy.email}
                                    </strong>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Widget de disponibilité stock ── */}
                {stockInfo && (
                    <div className={`dp-stock-widget ${stockInfo.disponible ? 'dispo' : 'rupture'}`}>
                        <div className="stock-widget-header">
                            <span className="stock-widget-icon">{stockInfo.disponible ? '✅' : '⚠️'}</span>
                            <div className="stock-widget-text">
                                <h3>{stockInfo.disponible ? 'Disponibilité en Stock OK' : 'Attention : Stock Insuffisant'}</h3>
                                <p>
                                    {stockInfo.disponible
                                        ? 'Tous les produits inclus dans ce pack disposent d’un stock suffisant.'
                                        : 'Certains produits recommandés manquent de quantité dans l’inventaire actuel.'}
                                </p>
                            </div>
                        </div>
                        {stockInfo.warnings && stockInfo.warnings.length > 0 && (
                            <ul className="stock-warnings-list">
                                {stockInfo.warnings.map((w, idx) => (
                                    <li key={idx}>• {w}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {/* ── Section Produits inclus par Catégorie ── */}
                <div className="dp-section">
                    <div className="dp-section-header">
                        <h2>📦 Produits Inclus dans le Pack ({pack.categories?.reduce((s, c) => s + (c.produits?.length || 0), 0)})</h2>
                        <span className="dp-section-count">{pack.categories?.length || 0} catégorie(s)</span>
                    </div>

                    {(pack.categories || []).map((cat, cIdx) => (
                        <div key={cIdx} className="dp-category-group">
                            <div className="dp-category-header">
                                <span className="cat-icon">📁</span>
                                <h3>{cat.categorieId?.label || 'Catégorie'}</h3>
                                <span className="cat-badge">{cat.produits?.length || 0} produit(s)</span>
                            </div>

                            <div className="dp-products-grid">
                                {(cat.produits || []).map((item, pIdx) => {
                                    const prod = item.produitId;
                                    const isLibre = !prod;
                                    const nom = prod?.nomProduit || item.nomProduit || 'Produit';
                                    const ref = prod?.reference || '—';
                                    const marqueName = prod?.marque?.name;
                                    const marqueLogo = prod?.marque?.logo;
                                    const imageSrc = prod?.imageProduit
                                        ? `${API_URL.replace('/api', '')}${prod.imageProduit}`
                                        : null;
                                    const isLineaire = prod?.modeGestionStock === 'lineaire';
                                    const prixHT = isLibre ? (Number(item.prixUnitaire) || 0) : getPrixVente(prod);
                                    const qte = item.quantiteRecommandee || 1;
                                    const totalHT = prixHT * qte;
                                    const tvaRate = prod?.TVA !== undefined ? Number(prod.TVA) : 19;
                                    const totalTTC = totalHT * (1 + tvaRate / 100);

                                    return (
                                        <div key={pIdx} className="dp-product-card">
                                            <div className="dp-prod-image-col">
                                                {imageSrc ? (
                                                    <img src={imageSrc} alt={nom} className="dp-prod-img" />
                                                ) : (
                                                    <div className="dp-prod-img-placeholder">☀️</div>
                                                )}
                                            </div>

                                            <div className="dp-prod-details-col">
                                                <div className="dp-prod-head">
                                                    <div>
                                                        <h4 className="dp-prod-name">{nom}</h4>
                                                        <div className="dp-prod-meta-pills">
                                                            {ref !== '—' && <span className="pill ref">Ref: {ref}</span>}
                                                            {isLineaire ? (
                                                                <span className="pill mode lineaire">📏 Découpe Linéaire</span>
                                                            ) : (
                                                                <span className="pill mode unite">📦 Unité</span>
                                                            )}
                                                            {marqueName && (
                                                                <span className="pill marque">
                                                                    {marqueLogo && <img src={`${API_URL.replace('/api', '')}${marqueLogo}`} alt="" className="marque-icon" />}
                                                                    {marqueName}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Détails de découpe si linéaire */}
                                                {isLineaire && item.decoupeLineaire && (
                                                    <div className="dp-lineaire-box">
                                                        <span className="lineaire-title">Spécification Découpe :</span>
                                                        <span className="lineaire-val">
                                                            {item.quantiteTriangles ? `${item.quantiteTriangles} triangle(s) · ` : ''}
                                                            {item.decoupeLineaire.nombrePieces || 1} pièce(s) × {fmt(item.decoupeLineaire.longueurPieceMetre)} m = <strong>{fmt(item.quantiteRecommandee)} m</strong>
                                                        </span>
                                                    </div>
                                                )}

                                                <div className="dp-prod-pricing-row">
                                                    <div className="price-item">
                                                        <small>Quantité</small>
                                                        <strong>{isLineaire ? `${fmt(qte)} m` : `${qte} u`}</strong>
                                                    </div>
                                                    <div className="price-item">
                                                        <small>Prix Unitaire HT</small>
                                                        <strong>{fmt(prixHT)} DT</strong>
                                                    </div>
                                                    <div className="price-item">
                                                        <small>TVA</small>
                                                        <strong>{tvaRate}%</strong>
                                                    </div>
                                                    <div className="price-item highlight">
                                                        <small>Sous-total HT</small>
                                                        <strong>{fmt(totalHT)} DT</strong>
                                                    </div>
                                                    <div className="price-item ttc">
                                                        <small>Total TTC</small>
                                                        <strong>{fmt(totalTTC)} DT</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Résumé Financier Complet ── */}
                <div className="dp-section">
                    <div className="dp-section-header">
                        <h2>📊 Résumé Financier du Pack</h2>
                    </div>

                    <div className="dp-financial-grid">
                        <div className="dp-finance-card ht">
                            <span className="fin-icon">📑</span>
                            <span className="fin-title">Total montant sans TVA</span>
                            <span className="fin-val">{fmt(financialSummary.sousTotalHT)} DT</span>
                            <span className="fin-desc">Somme des prix unitaires HT × quantités</span>
                        </div>

                        <div className="dp-finance-card tva">
                            <span className="fin-icon">🏛️</span>
                            <span className="fin-title">Total montant TVA</span>
                            <span className="fin-val">{fmt(financialSummary.totalTVA)} DT</span>
                            <span className="fin-desc">Montant total de TVA appliqué (ex. 19%)</span>
                        </div>

                        <div className="dp-finance-card highlight">
                            <span className="fin-icon">🏷️</span>
                            <span className="fin-title">Montant final TTC</span>
                            <span className="fin-val">{fmt(financialSummary.totalTTC)} DT</span>
                            <span className="fin-desc">Total HT + Total TVA</span>
                        </div>

                        <div className="dp-finance-card final">
                            <span className="fin-icon">⭐</span>
                            <span className="fin-title">Prix Final Administrateur</span>
                            <span className="fin-val">{fmt(financialSummary.prixFinal)} DT</span>
                            <span className="fin-desc">Prix de vente officiel du pack en boutique</span>
                        </div>
                    </div>
                </div>

                {/* ── Actions Inférieures ── */}
                <div className="dp-bottom-actions-bar">
                    <button className="dp-btn-back" onClick={() => onNavigate('packsAdmin')}>
                        ← Retour à la liste
                    </button>
                    <div className="dp-action-buttons">
                        {hasPermission('produits', 'update') && (
                            <button className="dp-btn-action edit" onClick={handleEdit} disabled={actionLoading}>
                                ✏️ Modifier
                            </button>
                        )}
                        {hasPermission('produits', 'create') && (
                            <button className="dp-btn-action duplicate" onClick={handleDuplicate} disabled={actionLoading}>
                                📋 Dupliquer
                            </button>
                        )}
                        {hasPermission('produits', 'update') && (
                            <button
                                className={`dp-btn-action toggle ${pack.statut === 'actif' ? 'deactivate' : 'activate'}`}
                                onClick={handleToggleStatus}
                                disabled={actionLoading}
                            >
                                {pack.statut === 'actif' ? '⏸️ Désactiver' : '▶️ Activer'}
                            </button>
                        )}
                        {hasPermission('produits', 'delete') && (
                            <button className="dp-btn-action delete" onClick={handleDelete} disabled={actionLoading}>
                                🗑️ Supprimer
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailPack;
